<template>
    <validation-observer ref="setup_commissions_form" v-slot="{ meta, setFieldValue, errors }">
        <modal
            :id="modalId"
            :title="`${isSetup ? 'Setup' : 'Update'} Commissions`"
            :ok-title="`${isSetup ? 'Save' : 'Update'}`"
            cancel-title="Close"
            :scrollable="false"
            :ok-disabled="!meta.valid"
            @ok.prevent="setupCommissions"
            @shown="setData"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-row class="mb-2">
                <b-col class="ml-1">Default:</b-col>
            </b-row>
            <b-row no-gutters>
                <b-col class="mr-2">
                    <form-select
                        v-model="metadata.subscriber.company"
                        floatingLabel="Company"
                        :options="[metadata.subscriber.company]"
                        :disabled="true"
                    />
                </b-col>
                <b-col>
                    <validation-provider
                        name="defaultCommission"
                        :rules="
                            !values.isFlat
                                ? 'validPositive|validFloat|maxValue:100|required'
                                : 'validPositive|validFloat|required'
                        "
                        
                    >
                        <form-input
                            min="0"
                            :max="!values.isFlat ? '100' : ''"
                            step="0.01"
                            type="number"
                            floatingLabel="Value"
                            v-model="values.defaultCommission"
                            :error="errors['defaultCommission']"
                            @change="(val) => setFieldValue('defaultCommission', val)"
                        />
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row class="mb-2">
                <b-col class="ml-1">Commission #1:</b-col>
            </b-row>
            <b-row no-gutters>
                <b-col class="mr-2">
                    <form-select
                        v-model="values.firstAgent"
                        floatingLabel="Agent Name"
                        :options="teamMemberOptions"
                        :disabled="teamMemberOptions.length == 1"
                        @change="onFirstAgentChange"
                    />
                </b-col>
                <b-col>
                    <validation-provider
                        name="firstCommission"
                        :rules="
                            !values.isFlat ? 'validFloat|maxValue:100|required' : 'validFloat|required'
                        "
                    >
                        <form-input
                            min="0"
                            :max="!values.isFlat ? '100' : ''"
                            step="0.01"
                            type="number"
                            floatingLabel="Value"
                            v-model="values.firstCommission"
                            :disabled="values.firstAgent == -1"
                            :error="errors['firstCommission']"
                            @change="(val) => setFieldValue('firstCommission', val)"
                        />
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row class="mb-2">
                <b-col class="ml-1">Commission #2:</b-col>
            </b-row>
            <b-row no-gutters class="mb-2">
                <b-col class="mr-2">
                    <form-select
                        v-model="values.secondAgent"
                        floatingLabel="Agent Name"
                        :options="teamMemberOptions"
                        :disabled="teamMemberOptions.length == 1"
                        @change="onSecondAgentChange"
                    />
                </b-col>
                <b-col>
                    <validation-provider
                        name="secondCommission"
                        :rules="
                            !values.isFlat ? 'validFloat|maxValue:100|required' : 'validFloat|required'
                        "
                    >
                        <form-input
                            min="0"
                            :max="!values.isFlat ? '100' : ''"
                            step="0.01"
                            type="number"
                            floatingLabel="Value"
                            v-model="values.secondCommission"
                            :disabled="values.secondAgent == -1"
                            :error="errors['secondCommission']"
                            @change="(val) => setFieldValue('secondCommission', val)"
                        />
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row v-if="isSetup" no-gutters>
                <b-col>
                    <form-select
                        v-model="values.techReservation"
                        floatingLabel="Property"
                        :options="propertyOptions"
                        :disabled="propertyOptions.length == 1"
                    />
                </b-col>
            </b-row>
            <b-row no-gutters>
                <b-col>
                    <form-select v-model="values.isFlat" floatingLabel="Type" :options="typeOptions" />
                </b-col>
            </b-row>
            <b-row no-gutters>
                <b-col>
                    <form-select
                        v-model="values.calculation"
                        floatingLabel="Calculation"
                        :options="calculationOptions"
                    />
                </b-col>
            </b-row>
        </modal>
    </validation-observer>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from '../../../../../api.jsx';
    import Modal from '../../../../generic/modal/modal.vue';
    import BusyModal from '../../../../generic/busy-modal/busy-modal.vue';
    import FormInput from '../../../../generic/form-input/form-input.vue';
    import FormSelect from '../../../../generic/form-select/form-select.vue';
    import ResponsiveButton from '../../../../generic/responsive-button/responsive-button.vue';
    import { GUEST_RESERVATION_SETUP_COMMISSIONS_ENDPOINT } from '../../../../../endpoints.js';
    import { formatResponseError } from '../../../../../utils/api';

    export default {
        name: 'SetupCommissionsModal',
        components: {
            Modal,
            BusyModal,
            FormInput,
            FormSelect,
            ResponsiveButton,
        },
        props: {
            modalId: String,
            isSetup: {
                type: Boolean,
                default: false,
            },
            initialValues: {
                type: Object,
                default: () => null,
            },
            reservation: {
                type: Object,
                default: () => ({}),
            },
        },
        data() {
            return {
                loading: false,
                values: this.getInitialValues(),
                typeOptions: [
                    { value: false, text: 'Percent %' },
                    { value: true, text: 'Fixed' },
                ],
            };
        },
        inject: ['showSuccessToast', 'showErrorToast'],
        computed: {
            ...mapGetters('guest', ['metadata']),
            guestId() {
                return parseInt(this.$route.params.id);
            },
            teamMemberOptions() {
                return this.metadata.team_member_choices;
            },
            propertyOptions() {
                return this.reservation.commission_property_choices;
            },
            calculationOptions() {
                let options = [
                    { value: 'GROSS', text: 'Gross' },
                    { value: 'NET', text: 'Net' },
                ];
                if (!this.values.accessChannelCommissionsToOwner) {
                    return options;
                }
                return [
                    ...options,
                    {
                        value: 'GROSS_LESS_CC',
                        text: 'Gross less Channel Commission',
                    },
                    {
                        value: 'NET_LESS_CC',
                        text: 'Net less Channel Commission',
                    },
                ];
            },
        },
        methods: {
            setData() {
                this.$refs.setup_commissions_form.setValues({
                    defaultCommission: this.values.defaultCommission,
                    firstCommission: this.values.firstCommission,
                    secondCommission: this.values.secondCommission,
                });
                this.$refs.setup_commissions_form.validate();
            },
            getInitialValues() {
                return {
                    firstAgent: this.initialValues?.details[1].detail.team_member || -1,
                    secondAgent: this.initialValues?.details[2].detail.team_member || -1,
                    firstCommission: this.initialValues?.details[1].detail.commission || 0,
                    secondCommission: this.initialValues?.details[2].detail.commission || 0,
                    defaultCommission: this.initialValues?.details[0].detail.commission || 0,
                    isFlat: this.initialValues?.is_flat || false,
                    calculation: this.initialValues?.commission_calculation || 'GROSS',
                    techReservation:
                        this.initialValues?.tech_res || this.reservation.commission_property_choices[0].value,
                    accessChannelCommissionsToOwner: this.initialValues?.assess_channel_commissions_to_owner || false,
                };
            },
            onFirstAgentChange(value) {
                if (value == -1) this.values.firstCommission = 0;
            },
            onSecondAgentChange(value) {
                if (value == -1) this.values.secondCommission = 0;
            },
            async setupCommissions() {
                this.loading = true;
                try {
                    await API.post(GUEST_RESERVATION_SETUP_COMMISSIONS_ENDPOINT(this.guestId), {
                        tech_res: this.values.techReservation,
                        details: [
                            {
                                position: 0,
                                team_member: this.metadata.subscriber.id,
                                commission: this.values.defaultCommission,
                            },
                            {
                                position: 1,
                                team_member: this.values.firstAgent,
                                commission: this.values.firstCommission,
                            },
                            {
                                position: 2,
                                team_member: this.values.secondAgent,
                                commission: this.values.secondCommission,
                            },
                        ],
                        is_flat: this.values.isFlat,
                        commission_calculation: this.values.calculation,
                    });
                    this.$bvModal.hide(this.modalId);
                    this.$emit('refreshReservation', () =>
                        this.showSuccessToast({
                            text: `Commissions successfully ${this.isSetup ? 'setup' : 'updated'}.`,
                        })
                    );
                } catch (err) {
                    const { generic } = formatResponseError(err);
                    this.showErrorToast({
                        text: generic || `Could not ${this.isSetup ? 'setup' : 'update'} commissions.`,
                    });
                } finally {
                    this.loading = false;
                }
            },
        },
        watch: {
            reservation() {
                this.values = this.getInitialValues();
            },
        },
    };
</script>
